<script setup lang="ts">
import { computed } from 'vue'
import { X } from '@lucide/vue'
import type { SemanticTone, TagColor } from '../../types'

import { CommonStatusMap } from '../../constants/status'

const props = withDefaults(
  defineProps<{
    color?: TagColor
    tone?: SemanticTone
    status?: string
    text?: string | number
    label?: string | number
    dot?: boolean
    closable?: boolean
    checkable?: boolean
    checked?: boolean
    bordered?: boolean
  }>(),
  {
    color: undefined,
    tone: undefined,
    status: undefined,
    text: undefined,
    label: '',
    dot: false,
    closable: false,
    checkable: false,
    checked: false,
    bordered: true
  }
)

const emit = defineEmits<{
  close: [event: MouseEvent]
  'update:checked': [checked: boolean]
  click: [event: MouseEvent]
}>()

const ANTD_STATUS_MAP: Record<string, string> = {
  success: 'success',
  processing: 'processing',
  info: 'processing',
  error: 'danger',
  danger: 'danger',
  warning: 'warning',
  default: 'neutral',
  neutral: 'neutral',
  muted: 'neutral',
  primary: 'primary'
}

const statusUpper = computed(() => (typeof props.status === 'string' ? props.status.trim().toUpperCase() : ''))
const statusLower = computed(() => (typeof props.status === 'string' ? props.status.trim().toLowerCase() : ''))

const resolvedTone = computed(() => {
  if (props.color) {
    const c = ANTD_STATUS_MAP[props.color] || props.color
    if (c.startsWith('#') || c.startsWith('rgb') || c.startsWith('var(')) return 'custom'
    return c
  }
  if (props.tone) {
    return ANTD_STATUS_MAP[props.tone] || props.tone
  }
  if (props.status) {
    if (ANTD_STATUS_MAP[statusLower.value]) return ANTD_STATUS_MAP[statusLower.value]
    if (CommonStatusMap[statusUpper.value]) {
      const mapped = CommonStatusMap[statusUpper.value].status
      return ANTD_STATUS_MAP[mapped] || mapped
    }
    return 'neutral'
  }
  return 'neutral'
})

const isCustomColor = computed(() => resolvedTone.value === 'custom')

const customStyle = computed(() => {
  if (!isCustomColor.value) return undefined
  const rawColor = String(props.color ?? props.tone)
  return {
    backgroundColor: `color-mix(in srgb, ${rawColor} 12%, transparent)`,
    borderColor: `color-mix(in srgb, ${rawColor} 32%, transparent)`,
    color: rawColor
  }
})

const displayContent = computed(() => {
  if (props.text !== undefined && props.text !== null && props.text !== '') return String(props.text)
  if (props.label !== undefined && props.label !== null && props.label !== '') return String(props.label)
  if (props.status) {
    if (CommonStatusMap[statusUpper.value]) return CommonStatusMap[statusUpper.value].text
    if (!ANTD_STATUS_MAP[statusLower.value]) return String(props.status)
  }
  return ''
})

function handleClose(event: MouseEvent) {
  event.stopPropagation()
  emit('close', event)
}

function handleClick(event: MouseEvent) {
  if (props.checkable) {
    emit('update:checked', !props.checked)
  }
  emit('click', event)
}
</script>

<template>
  <span
    class="rr-tag"
    :class="[
      `is-${resolvedTone}`,
      {
        'is-checkable': checkable,
        'is-checked': checked,
        'has-dot': dot,
        'no-border': !bordered
      }
    ]"
    :style="customStyle"
    @click="handleClick"
  >
    <slot name="icon">
      <i v-if="dot" class="rr-tag__dot" aria-hidden="true" />
    </slot>
    <span class="rr-tag__content">
      <slot>{{ displayContent }}</slot>
    </span>
    <button
      v-if="closable"
      type="button"
      class="rr-tag__close-btn"
      aria-label="关闭标签"
      @click="handleClose"
    >
      <X :size="11" aria-hidden="true" />
    </button>
  </span>
</template>

<style scoped>
.rr-tag {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 5px;
  height: 24px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  padding: 0 7px;
  white-space: nowrap;
  line-height: 1;
  transition: all 120ms ease;
}

.rr-tag__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.rr-tag__content {
  display: inline-flex;
  align-items: center;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}

.rr-tag__close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  min-width: 14px !important;
  min-height: 14px !important;
  max-width: 14px !important;
  max-height: 14px !important;
  margin: 0 0 0 2px !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 50%;
  background: transparent !important;
  color: inherit !important;
  cursor: pointer;
  line-height: 1;
  opacity: 0.7;
}

.rr-tag__close-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.08) !important;
}

/* Tone Variants mapped to tokens.md (soft background + solid text + border) */
.rr-tag.is-primary {
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}

.rr-tag.is-success {
  border-color: var(--rr-color-success-border);
  background: var(--rr-color-success-soft);
  color: var(--rr-color-success);
}

.rr-tag.is-warning {
  border-color: var(--rr-color-warning-border);
  background: var(--rr-color-warning-soft);
  color: var(--rr-color-warning);
}

.rr-tag.is-danger {
  border-color: var(--rr-color-danger-border);
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
}

.rr-tag.is-info,
.rr-tag.is-processing {
  border-color: var(--rr-color-info-border);
  background: var(--rr-color-info-soft);
  color: var(--rr-color-info);
}

.rr-tag.is-default,
.rr-tag.is-neutral {
  border-color: var(--rr-color-border-strong);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}

.rr-tag.no-border {
  border-color: transparent;
}

.rr-tag.is-checkable {
  cursor: pointer;
  user-select: none;
}
.rr-tag.is-checkable:hover {
  opacity: 0.85;
}
.rr-tag.is-checkable.is-checked {
  background: var(--rr-color-primary);
  border-color: var(--rr-color-primary);
  color: var(--rr-color-surface);
}
</style>
